import Phaser from 'phaser';

const SKY_TOP = 0x1e3a8a;
const SKY_MID = 0x3730a3;
const SKY_BOT = 0x14532d;

const HILLS_FAR = 0x1e293b;
const HILLS_MID = 0x134e4a;
const HILLS_NEAR = 0x052e2b;

const MANSION_BODY = 0x0f172a;
const MANSION_GOLD = 0xfbbf24;
const MANSION_WINDOW = 0xfde047;
const X_MARK = 0xef4444;
const STAR = 0xffffff;
const CLOUD = 0x475569;

export function drawMutantIslandSkyBackground(
  g: Phaser.GameObjects.Graphics,
  w: number,
  h: number,
) {
  g.clear();
  const step = Math.max(1, Math.floor(h / 40));
  for (let y = 0; y < h; y += step) {
    const t = y / h;
    let r: number, gC: number, b: number;
    if (t < 0.5) {
      const u = t / 0.5;
      r = lerp((SKY_TOP >> 16) & 0xff, (SKY_MID >> 16) & 0xff, u);
      gC = lerp((SKY_TOP >> 8) & 0xff, (SKY_MID >> 8) & 0xff, u);
      b = lerp(SKY_TOP & 0xff, SKY_MID & 0xff, u);
    } else {
      const u = (t - 0.5) / 0.5;
      r = lerp((SKY_MID >> 16) & 0xff, (SKY_BOT >> 16) & 0xff, u);
      gC = lerp((SKY_MID >> 8) & 0xff, (SKY_BOT >> 8) & 0xff, u);
      b = lerp(SKY_MID & 0xff, SKY_BOT & 0xff, u);
    }
    g.fillStyle((r << 16) | (gC << 8) | b, 1);
    g.fillRect(0, y, w, step + 1);
  }

  const rand = mulberry32(808);
  g.fillStyle(STAR, 0.75);
  const starCount = Math.max(10, Math.floor(w / 60));
  for (let i = 0; i < starCount; i++) {
    const sx = rand() * w;
    const sy = rand() * h * 0.3;
    g.fillCircle(sx, sy, rand() < 0.2 ? 1.5 : 1);
  }

  const cloudCount = Math.max(3, Math.floor(w / 280));
  for (let i = 0; i < cloudCount; i++) {
    const cx = rand() * w;
    const cy = h * 0.18 + rand() * h * 0.12;
    const cw = 60 + rand() * 40;
    g.fillStyle(CLOUD, 0.5);
    g.fillEllipse(cx, cy, cw, cw * 0.28);
  }

  const xCloudX = Math.floor(w * 0.72);
  const xCloudY = Math.floor(h * 0.18);
  const xSize = Math.min(48, Math.floor(w * 0.06));
  g.lineStyle(6, X_MARK, 0.55);
  g.beginPath();
  g.moveTo(xCloudX - xSize, xCloudY - xSize);
  g.lineTo(xCloudX + xSize, xCloudY + xSize);
  g.moveTo(xCloudX + xSize, xCloudY - xSize);
  g.lineTo(xCloudX - xSize, xCloudY + xSize);
  g.strokePath();
}

export function drawMutantIslandSilhouette(
  g: Phaser.GameObjects.Graphics,
  w: number,
  h: number,
  seed = 808,
) {
  const rand = mulberry32(seed);
  const baselineY = Math.floor(h * 0.78);

  drawRollingHills(g, w, h, baselineY, HILLS_FAR, 0.2, rand, 3);
  drawRollingHills(g, w, h, baselineY, HILLS_MID, 0.12, rand, 5);
  drawMansion(g, w, baselineY);
  drawRollingHills(g, w, h, baselineY, HILLS_NEAR, 0.06, rand, 6);
}

function drawRollingHills(
  g: Phaser.GameObjects.Graphics,
  w: number,
  h: number,
  baselineY: number,
  color: number,
  offsetFrac: number,
  rand: () => number,
  count: number,
) {
  const hillY = baselineY + h * offsetFrac;
  g.fillStyle(color, 1);
  g.fillRect(0, hillY, w, h - hillY + 20);
  for (let i = 0; i < count; i++) {
    const cx = (i / count) * w + rand() * (w / count);
    const cy = hillY + 8 - rand() * 12;
    const rx = 120 + rand() * 160;
    const ry = 38 + rand() * 20;
    g.fillEllipse(cx, cy, rx, ry);
  }
}

function drawMansion(g: Phaser.GameObjects.Graphics, w: number, baselineY: number) {
  const cx = Math.floor(w / 2);
  const mansionW = Math.min(280, Math.floor(w * 0.4));
  const mansionH = 120;
  const mansionX = cx - mansionW / 2;
  const mansionY = baselineY - mansionH;

  g.fillStyle(MANSION_BODY, 1);
  g.fillRect(mansionX, mansionY, mansionW, mansionH);

  const towerW = 28;
  const towerH = 50;
  g.fillRect(mansionX - 4, mansionY - towerH, towerW, mansionH + towerH);
  g.fillRect(mansionX + mansionW - towerW + 4, mansionY - towerH, towerW, mansionH + towerH);

  g.fillStyle(MANSION_BODY, 1);
  g.beginPath();
  g.moveTo(mansionX - 10, mansionY - towerH);
  g.lineTo(mansionX - 4 + towerW / 2, mansionY - towerH - 20);
  g.lineTo(mansionX + towerW - 2, mansionY - towerH);
  g.closePath();
  g.fillPath();
  g.beginPath();
  g.moveTo(mansionX + mansionW - towerW - 2, mansionY - towerH);
  g.lineTo(mansionX + mansionW - towerW + 4 + towerW / 2, mansionY - towerH - 20);
  g.lineTo(mansionX + mansionW + 10, mansionY - towerH);
  g.closePath();
  g.fillPath();

  g.beginPath();
  g.moveTo(mansionX, mansionY);
  g.lineTo(cx, mansionY - 40);
  g.lineTo(mansionX + mansionW, mansionY);
  g.closePath();
  g.fillPath();

  g.fillStyle(MANSION_GOLD, 1);
  g.fillRect(mansionX, mansionY - 3, mansionW, 2);

  const winRows = 2;
  const winCols = 5;
  for (let r = 0; r < winRows; r++) {
    for (let c = 0; c < winCols; c++) {
      const wx = mansionX + 24 + c * ((mansionW - 48) / (winCols - 1)) - 4;
      const wy = mansionY + 22 + r * 40;
      g.fillStyle(MANSION_WINDOW, 0.9);
      g.fillRect(wx, wy, 8, 14);
    }
  }

  g.fillStyle(MANSION_WINDOW, 1);
  g.fillCircle(cx, mansionY - 22, 6);
  g.lineStyle(2, X_MARK, 0.9);
  g.beginPath();
  g.moveTo(cx - 4, mansionY - 26);
  g.lineTo(cx + 4, mansionY - 18);
  g.moveTo(cx + 4, mansionY - 26);
  g.lineTo(cx - 4, mansionY - 18);
  g.strokePath();

  g.fillStyle(MANSION_WINDOW, 0.95);
  const doorW = 16;
  const doorH = 26;
  g.fillRect(cx - doorW / 2, baselineY - doorH, doorW, doorH);
}

function lerp(a: number, b: number, t: number): number {
  return Math.round(a + (b - a) * t);
}

function mulberry32(seed: number) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
